import React, { useState } from 'react';
import { X, Star, Pencil, Calendar, Clock, Zap, MessageSquare, Check, Trash2 } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import Avatar from './Avatar';
import { ptsFor } from '../utils/points';

// Tap-to-rate stars — replaces a plain range slider so grading feels like a
// quick reaction instead of a form field.
const StarRating = ({ value, onChange, activeClass = 'text-mint fill-mint', size = 26 }) => (
  <div className="flex gap-1.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`transition-transform active:scale-90 ${n <= value ? activeClass : 'text-ink-fg/20'}`}
      >
        <Star size={size} className={n <= value ? 'fill-current' : ''} />
      </button>
    ))}
  </div>
);

const ChoreLogDetailsModal = ({ log, currentUserId, onClose, onGradeSubmit, onEditLog, onDeleteLog, onEditGrade }) => {
  // ─── View state: 'view' | 'editLog' | 'editGrade' ───
  const [mode, setMode] = useState('view');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Grade form state (for both new and edit)
  const [grade, setGrade] = useState(5);
  const [observation, setObservation] = useState('');

  // Log edit state
  const [editMinutes, setEditMinutes] = useState(log?.actualMinutes || 10);
  const [editDifficulty, setEditDifficulty] = useState(log?.actualDifficulty || 2);
  const [editNote, setEditNote] = useState(log?.additionalUserNote || '');
  const [editDoneAt, setEditDoneAt] = useState(() => {
    if (!log?.doneAt) return '';
    const d = new Date(log.doneAt);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  });

  if (!log) return null;

  const didCurrentUserParticipate = log.user.some(u => u.id === currentUserId);
  const hasCurrentUserGraded = log.othersUsersGrade?.some(g => g.userId === currentUserId);
  const myGrade = log.othersUsersGrade?.find(g => g.userId === currentUserId);

  const grades = log.othersUsersGrade || [];
  const averageGrade = grades.length > 0
    ? (grades.reduce((acc, curr) => acc + curr.grade, 0) / grades.length).toFixed(1)
    : null;

  const handleNewGradeSubmit = (e) => {
    e.preventDefault();
    onGradeSubmit(log.id, { grade, observation });
  };

  const handleEditLogSubmit = (e) => {
    e.preventDefault();
    onEditLog(log.id, {
      actualMinutes: parseInt(editMinutes),
      actualDifficulty: parseInt(editDifficulty),
      additionalUserNote: editNote,
      doneAt: new Date(editDoneAt).toISOString(),
    });
  };

  const handleEditGradeSubmit = (e) => {
    e.preventDefault();
    onEditGrade(log.id, { grade, observation });
  };

  // Pre-fill grade edit form when switching to edit grade mode
  const enterEditGradeMode = () => {
    if (myGrade) {
      setGrade(myGrade.grade);
      setObservation(myGrade.observation || '');
    }
    setMode('editGrade');
  };

  return (
    <div className="fixed inset-0 bg-ink-overlay/70 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-0 md:p-4 animate-in fade-in duration-200">
      <div className="bg-ink-card2 rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col border border-white/10 transform transition-transform duration-300 translate-y-0">

        {/* Header */}
        <div className="flex justify-between items-center gap-3 p-5 md:p-6 border-b border-white/10 bg-ink-card2 sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-serif text-ink-fg truncate">"{log.choreTitle}"</h2>
            {mode !== 'view' && (
              <p className="text-xs font-mono font-medium text-mint uppercase tracking-widest mt-0.5">
                {mode === 'editLog' ? 'Edit Entry' : 'Edit Review'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode !== 'view' && (
              <button
                onClick={() => setMode('view')}
                className="text-xs font-bold px-3 py-1.5 rounded-xl bg-ink-card3 text-ink-fg/60 hover:bg-ink-card transition-colors"
              >
                Cancel
              </button>
            )}
            <button onClick={onClose} className="text-ink-fg/40 hover:text-ink-fg/70 bg-ink-card3 hover:bg-ink-card rounded-full p-2 transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 md:p-6 overflow-y-auto flex-grow space-y-5 custom-scrollbar">

          {/* ════ VIEW MODE ════ */}
          {mode === 'view' && (
            <>
              {/* Who, when, and the points this entry is worth */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Avatar user={log.user?.[0]} size="w-5 h-5" textSize="text-[8px]" />
                  <p className="text-xs font-mono text-ink-fg/45 uppercase tracking-wide truncate">
                    {log.user?.length > 0 ? log.user.map(u => u.name).join(', ') : 'Someone'}
                    {' · '}
                    {new Date(log.doneAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, {new Date(log.doneAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' · '}
                    {log.actualMinutes} MIN
                    {' · '}
                    <span className="text-mint">+{ptsFor(log.actualMinutes, log.actualDifficulty)}</span>
                  </p>
                </div>
                {didCurrentUserParticipate && (
                  <button
                    onClick={() => setMode('editLog')}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-ink-card3 border border-mint/20 text-mint rounded-xl hover:bg-mint/10 transition-all shadow-sm flex-shrink-0"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                )}
              </div>

              {/* Submitter Notes */}
              {log.additionalUserNote && (
                <div>
                  <h3 className="text-xs font-mono font-medium text-ink-fg/40 uppercase tracking-wider mb-2">Notes</h3>
                  <p className="text-sm bg-ink-card3 p-4 rounded-2xl italic text-ink-fg/70 border border-white/10">"{log.additionalUserNote}"</p>
                </div>
              )}

              {/* Reviews List */}
              <div className="pt-2 border-t border-white/10">
                <h3 className="text-xs font-mono font-medium text-ink-fg/45 uppercase tracking-widest mb-3">
                  Reviews{averageGrade ? ` · Avg ${averageGrade}` : ''}
                </h3>
                {grades.length === 0 ? (
                  <p className="text-sm text-ink-fg/40 italic text-center py-4 bg-ink-card3 rounded-2xl border border-white/10">No reviews yet.</p>
                ) : (
                  <div className="space-y-3">
                    {grades.map(g => (
                      <div key={g.id} className="bg-ink-card p-4 rounded-2xl text-sm border border-white/10 shadow-sm">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-bold text-ink-fg/90">{g.user?.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="flex items-center text-mint">
                              {Array.from({ length: g.grade }).map((_, i) => (
                                <Star key={i} size={13} className="fill-current" />
                              ))}
                            </span>
                            {/* Edit button for the current user's own review */}
                            {g.userId === currentUserId && (
                              <button
                                onClick={enterEditGradeMode}
                                className="text-ink-fg/40 hover:text-mint p-1 rounded-lg hover:bg-mint/10 transition-all"
                                title="Edit your review"
                              >
                                <Pencil size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                        {g.observation && <p className="text-ink-fg/60 leading-relaxed">"{g.observation}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Grade Form */}
              {!didCurrentUserParticipate && !hasCurrentUserGraded && (
                <div className="border-t border-white/10 pt-5 pb-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-mono font-medium text-ink-fg/45 uppercase tracking-widest">Your Grade</h3>
                  </div>
                  <form onSubmit={handleNewGradeSubmit} className="space-y-4">
                    <div className="bg-ink-card3 p-4 rounded-2xl border border-white/10">
                      <div className="mb-4">
                        <StarRating value={parseInt(grade)} onChange={(n) => setGrade(n)} />
                      </div>
                      <div>
                        <textarea
                          value={observation}
                          onChange={e => setObservation(e.target.value)}
                          className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-3 focus:ring-2 focus:ring-mint outline-none transition-all resize-none placeholder:text-ink-fg/30"
                          rows="2"
                          placeholder="Say something nice (optional)"
                        />
                      </div>
                    </div>
                    <button type="submit" className="w-full py-4 bg-mint text-ink-shell rounded-2xl hover:brightness-95 font-bold shadow-md transition-all active:scale-95">
                      Submit grade
                    </button>
                  </form>
                </div>
              )}
            </>
          )}

          {/* ════ EDIT LOG MODE ════ */}
          {mode === 'editLog' && (
            <form onSubmit={handleEditLogSubmit} className="space-y-4">
              <p className="text-sm text-ink-fg/50">Update the details of your chore entry.</p>

              <div className="space-y-4 bg-ink-card3 p-4 rounded-2xl border border-white/10">
                {/* Date */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">
                    <Calendar size={12} /> Done on
                  </label>
                  <input
                    type="datetime-local"
                    value={editDoneAt}
                    onChange={e => setEditDoneAt(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-mint outline-none"
                  />
                </div>
                {/* Time */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">
                    <Clock size={12} /> Time Spent (Minutes)
                  </label>
                  <input
                    type="number" min="1"
                    value={editMinutes}
                    onChange={e => setEditMinutes(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-mint outline-none"
                    required
                  />
                </div>
                {/* Difficulty */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">
                    <Zap size={12} /> Difficulty — <span className="text-mint font-extrabold">{editDifficulty}</span>
                  </label>
                  <input
                    type="range" min="1" max="5"
                    value={editDifficulty}
                    onChange={e => setEditDifficulty(e.target.value)}
                    className="w-full accent-mint"
                  />
                  <div className="flex justify-between text-xs text-ink-fg/40 mt-1 px-1 font-medium">
                    <span>1 (Easy)</span><span>3</span><span>5 (Hard)</span>
                  </div>
                </div>
                {/* Note */}
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">
                    <MessageSquare size={12} /> Notes (Optional)
                  </label>
                  <textarea
                    value={editNote}
                    onChange={e => setEditNote(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-3 focus:ring-2 focus:ring-mint outline-none resize-none placeholder:text-ink-fg/30"
                    rows="2"
                    placeholder="Any issues or observations?"
                  />
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-mint text-ink-shell rounded-2xl hover:brightness-95 font-bold shadow-md transition-all active:scale-95 flex items-center justify-center gap-2">
                <Check size={18} /> Save Changes
              </button>
              {onDeleteLog && (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  className="w-full py-3 bg-danger/10 text-danger rounded-2xl hover:bg-danger/20 font-bold border border-danger-border/30 transition-all active:scale-95 flex items-center justify-center gap-2 text-sm"
                >
                  <Trash2 size={15} /> Delete Entry
                </button>
              )}
            </form>
          )}

          {confirmingDelete && (
            <ConfirmDialog
              title="Delete this entry?"
              message={`${log.user?.length > 0 ? log.user.map(u => u.name).join(', ') : 'Someone'}'s "${log.choreTitle}" from ${new Date(log.doneAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, plus its ${grades.length} review${grades.length === 1 ? '' : 's'}.`}
              impact={
                <>
                  <Avatar user={log.user?.[0]} size="w-8 h-8" textSize="text-xs" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink-fg/90">
                      {log.user?.[0]?.name || 'Someone'} loses {ptsFor(log.actualMinutes, log.actualDifficulty)} points
                    </p>
                    <p className="text-[10px] font-mono text-ink-fg/40 mt-0.5">{log.actualMinutes} min × difficulty {log.actualDifficulty}</p>
                  </div>
                </>
              }
              confirmLabel="Delete"
              cancelLabel="Keep it"
              onCancel={() => setConfirmingDelete(false)}
              onConfirm={() => { setConfirmingDelete(false); onDeleteLog(log.id); }}
            />
          )}

          {/* ════ EDIT GRADE MODE ════ */}
          {mode === 'editGrade' && (
            <form onSubmit={handleEditGradeSubmit} className="space-y-4">
              <p className="text-sm text-ink-fg/50">Update your review for this chore.</p>

              <div className="bg-ink-card3 p-4 rounded-2xl border border-white/10 space-y-4">
                <div>
                  <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Your Grade</label>
                  <StarRating value={parseInt(grade)} onChange={(n) => setGrade(n)} activeClass="text-amber-400 fill-amber-400" />
                </div>
                <div>
                  <textarea
                    value={observation}
                    onChange={e => setObservation(e.target.value)}
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg rounded-xl px-4 py-3 focus:ring-2 focus:ring-amber-400 outline-none resize-none placeholder:text-ink-fg/30"
                    rows="3"
                    placeholder="Say something nice (optional)"
                  />
                </div>
              </div>

              <button type="submit" className="w-full py-4 bg-amber-400 text-ink-shell rounded-2xl hover:brightness-95 font-bold shadow-md transition-all active:scale-95 flex items-center justify-center gap-2">
                <Check size={18} /> Update Review
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default ChoreLogDetailsModal;
